// CreateVideo.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  urlValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createVideo,
  getAllVideos,
  getVideoByID,
  updateVideo,
} from "./services/video.service";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { clearVideoDetails } from "./videoSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import moment from "moment";

const CreateVideo: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const VideoID = useAppSelector((state) => state.drawer.data);
  const VideoDetail = useAppSelector((state) => state.video.VideoDetail);
  const loading = useAppSelector((state) => state.video.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [VideoDetail]);

  useEffect(() => {
    if (VideoID) {
      dispatch(getVideoByID(VideoID));
    }
    return () => {
      dispatch(clearVideoDetails());
    };
  }, [VideoID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      VideoURL: values.VideoURL ?? "",
      Tagline: values.Tagline ?? "",
      Date: values.Date ? moment(values.Date).format("YYYY-MM-DD") : "",
      ShortDescription: values.ShortDescription ?? "",
      SequenceNo: values.SequenceNo ?? 0,
      IsDisplayHome: values.IsDisplayHome ?? false,
      IsActive: values.IsActive === 1,
    };

    try {
      if (VideoID) {
        const editPayload = {
          ...formData,
          ID: VideoID,
        };

        const response = await dispatch(updateVideo(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllVideos());
          dispatch(clearVideoDetails());
        }
      } else {
        const response = await dispatch(createVideo(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllVideos());
          dispatch(clearVideoDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={VideoDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 20,
                }}
              >
                {" "}
                <Typography.h4 className="m-0">
                  {VideoID ? "Update Video" : "Add Video"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="Title"
                  label="Video Title"
                  maxLength="200"
                  placeholder="Enter video title"
                  component={FormTextField}
                  validator={requiredValidator}
                  astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="VideoURL"
                  label="YouTube Video URL"
                  maxLength="500"
                  placeholder="https://www.youtube.com/watch?v=..."
                  component={FormTextField}
                  validator={urlValidator}
                  astrike={true}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="Tagline"
                  label="Tagline"
                  maxLength="300"
                  placeholder="Enter video tagline"
                  component={FormTextField}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="Date"
                  label="Date"
                  placeholder="Select date"
                  component={FormDatePicker}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="ShortDescription"
                  label="Short Description"
                  maxLength="500"
                  placeholder="Enter short description"
                  component={FormTextArea}
                  style={{ height: 100 }}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="SequenceNo"
                  label="Sequence Number"
                  placeholder="Enter sequence number"
                  component={FormNumericTextField}
                  min={0}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={1}
                style={{
                  width: "100%",
                  margin: "18px 0px 0px",
                  padding: "0px 10px 8px",
                  fontWeight: 700,
                  background: "whitesmoke",
                  borderRadius: 5,
                  height: 48,
                }}
              >
                <Field
                  name="IsDisplayHome"
                  label="Display in Home"
                  component={FormCheckbox}
                />
              </GridLayoutItem>

              <GridLayoutItem>
                <Field
                  name="IsActive"
                  label="Status"
                  placeholder="Select status"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => ({
                    value: status?.value,
                    label: status?.label,
                  }))}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  marginTop: "20px",
                  textAlign: "end",
                }}
              >
                <div>
                  <ButtonWithLoading
                    label={VideoID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={() => dispatch(closed())}
                  >
                    Cancel
                  </Button>
                </div>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateVideo;
